import axios from "axios";
import React from "react";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { NavLink } from "react-router-dom";
import Swal from "sweetalert2";
import Empty from "../../assets/images/appImages/category.png";
import { useAppSelector } from "../../redux/hook";
import { formatCurrency, getImage } from "../../utils/Utils";
import { useLazyFavoriteByUserQuery } from "../../utils/api/favories/favorie.api";
import { useGetVendeurByApiKeyQuery } from "../../utils/api/product/product.api";
import { ApiBaseUrl, VendeurApikey } from "../../utils/http";
import { Color } from "../../utils/theme";

interface BestSellerCardProps {
  item: any;
  onFavoriteToggle?: () => void;
}

/**
 * Composant de carte produit pour la section "Nos Best Sellers"
 * Design exact selon les spécifications avec badge, cœur, nom, prix et bouton centrés
 */
export const BestSellerCard: React.FC<BestSellerCardProps> = ({
  item,
  onFavoriteToggle,
}) => {
  const { user, token } = useAppSelector((s) => s?.user);
  const { data: vendeur } = useGetVendeurByApiKeyQuery(VendeurApikey);
  const Vendeur = vendeur || { devise: 'XOF' };

  // Récupération de l'image du produit
  const imgProd =
    item?.type == "taille_unique"
      ? item?.images[0]?.image
      : item?.variations && item?.variations[0]?.images
        ? item?.variations[0]?.images[0]?.image
        : null;

  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const [findBySlug, { data, isLoading }] = useLazyFavoriteByUserQuery();

  React.useEffect(() => {
    if (user?.slug) {
      findBySlug(user?.slug as string);
    }
  }, [user]);

  const handleAddFavoris = (id: number) => {
    const favorie = data?.find((x) => x?.produit?.id == id);

    const fd = new FormData();
    fd.append("user", user?.id);
    fd.append("produit", id.toString());

    if (favorie) {
      axios
        .delete(ApiBaseUrl + `/api/favoris/${favorie?.slug}`, config)
        .then(() => {
          findBySlug(user?.slug as string);
          onFavoriteToggle?.();
        })
        .catch((error) => {
          console.error(`Error: ${error}`);
        });
    } else {
      axios
        .post(ApiBaseUrl + "/api/favoris/", fd, config)
        .then(() => {
          findBySlug(user?.slug as string);
          onFavoriteToggle?.();
        })
        .catch((error) => {
          console.error(`Error: ${error}`);
        });
    }
  };

  const isFavorite = (id: number): boolean => {
    return !!data?.find((x) => x?.produit?.id == id);
  };

  const onToggleFavorite = (id: number) => {
    if (!user?.slug) {
      Swal.fire({
        icon: "info",
        title: `Veuillez vous connecter pour ajouter un produit aux favoris.`,
        iconColor: Color.success,
        showConfirmButton: false,
        timer: 6000,
      });
    } else {
      handleAddFavoris(id);
    }
  };

  // Calcul du taux de réduction
  const reductionTaux =
    item?.categorie?.promotion?.taux ||
    item?.sous_categorie?.promotion?.taux ||
    item?.promotion?.taux;

  const hasPromotion =
    item?.categorie?.promotion?.is_active ||
    item?.sous_categorie?.promotion?.is_active ||
    item?.promotion?.is_active;

  // Calcul du prix avec réduction
  const prixFinal = hasPromotion
    ? item?.prix - (item?.prix * reductionTaux) / 100
    : item?.prix;

  return (
    <div className="best-seller-card">
      <NavLink
        to={`/produit/${item?.slug}`}
        state={item}
        className="best-seller-card-link"
      >
        {/* Conteneur de l'image */}
        <div className="best-seller-card-image-wrapper">
          {/* Badge de réduction - positionné à droite, collé au bord, pas de radius à droite */}
          {hasPromotion && reductionTaux && (
            <div className="best-seller-card-badge-right">
              -{reductionTaux}%
            </div>
          )}
          <img
            loading="lazy"
            src={imgProd ? getImage(imgProd) : Empty}
            alt={item?.nom}
            className="best-seller-card-image"
          />
          {/* Icône favoris (cœur) - positionnée sur l'image */}
          <button
            disabled={isLoading}
            className="best-seller-card-favorite"
            aria-label="Ajouter aux favoris"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleFavorite(item?.id);
            }}
          >
            {isFavorite(item?.id) ? (
              <AiFillHeart className="best-seller-card-favorite-icon best-seller-card-favorite-icon-filled" />
            ) : (
              <AiOutlineHeart className="best-seller-card-favorite-icon" />
            )}
          </button>
        </div>

        {/* Nom du produit */}
        <div className="best-seller-card-body">
          <h4 className="best-seller-card-name">{item?.nom}</h4>

          {/* Prix */}
          <div className="best-seller-card-price">
            {hasPromotion ? (
              <>
                <span className="best-seller-card-price-final">
                  {formatCurrency(prixFinal, Vendeur?.devise)}
                </span>
                <span className="best-seller-card-price-original">
                  {formatCurrency(item?.prix, Vendeur?.devise)}
                </span>
              </>
            ) : (
              <span className="best-seller-card-price-final">
                {formatCurrency(item?.prix, Vendeur?.devise)}
              </span>
            )}
          </div>

          {/* Bouton Acheter */}
          <div className="best-seller-card-button-wrapper">
            {item?.stock > 0 ? (
              <NavLink
                to={`/produit/${item?.slug}`}
                className="best-seller-card-button"
                onClick={(e) => e.stopPropagation()}
              >
                Acheter
              </NavLink>
            ) : (
              <button className="best-seller-card-button best-seller-card-button-disabled" disabled>
                Acheter
              </button>
            )}
          </div>
        </div>
      </NavLink>
    </div>
  );
};
